import { useNavigate, useLocation } from 'react-router-dom'
import { House, Map, Radio, Store, Bookmark, User, LogIn, LogOut, X } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

const C = { teal: '#0F5C5C', ink: '#202523', ink2: '#6D7471', border: '#E1E5E3' }
const FH = "'Plus Jakarta Sans', sans-serif"
const FB = "'Inter', sans-serif"

const NAV = [
  { path: '/', icon: House, label: 'Inicio' },
  { path: '/map', icon: Map, label: 'Mapa' },
  { path: '/now', icon: Radio, label: 'Ahora' },
  { path: '/following', icon: Store, label: 'Siguiendo', auth: true },
  { path: '/saved', icon: Bookmark, label: 'Guardados', auth: true },
  { path: '/profile', icon: User, label: 'Perfil', auth: true },
]

interface Props {
  onClose: () => void
}

export default function MobileDrawer({ onClose }: Props) {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const isActive = (p: string) => (p === '/' ? pathname === '/' : pathname.startsWith(p))

  const go = (path: string) => {
    navigate(path)
    onClose()
  }

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99,
          background: 'rgba(0,0,0,.5)',
          backdropFilter: 'blur(2px)',
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          width: 300,
          zIndex: 100,
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          boxShadow: '6px 0 40px rgba(0,0,0,.18)',
          animation: 'pxSlideIn .22s ease',
        }}
      >
        {/* Header del drawer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 20px 16px',
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: '50% 50% 50% 4px',
                background: C.teal,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <span style={{ color: '#fff', fontSize: 18 }}>⚡</span>
            </div>
            <span
              style={{
                fontFamily: FH,
                fontWeight: 800,
                fontSize: 22,
                color: C.teal,
                letterSpacing: '-0.02em',
              }}
            >
              Párchala
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              border: `1px solid ${C.border}`,
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: C.ink2,
            }}
          >
            <X size={18} color={C.ink2} />
          </button>
        </div>

        {/* Nav items */}
        <nav style={{ flex: 1, padding: '12px 12px 0' }}>
          {NAV.map(({ path, icon: Icon, label, auth }) => {
            const active = isActive(path)
            return (
              <button
                key={path}
                onClick={() => {
                  if (auth && !user) {
                    go('/login')
                    return
                  }
                  go(path)
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  width: '100%',
                  padding: '14px 16px',
                  marginBottom: 4,
                  borderRadius: 14,
                  border: 'none',
                  textAlign: 'left',
                  fontFamily: FB,
                  fontSize: 16,
                  fontWeight: active ? 700 : 500,
                  background: active ? `${C.teal}12` : 'transparent',
                  color: active ? C.teal : C.ink,
                  cursor: 'pointer',
                }}
              >
                <Icon size={22} color={active ? C.teal : C.ink2} style={{ flexShrink: 0 }} />
                <span>{label}</span>
                {path === '/now' && (
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: 99,
                      background: '#D85F5F',
                      marginLeft: 'auto',
                      animation: 'pxPulse 1.4s infinite',
                      flexShrink: 0,
                    }}
                  />
                )}
              </button>
            )
          })}
        </nav>

        {/* Footer */}
        <div style={{ padding: '12px 12px 32px', borderTop: `1px solid ${C.border}` }}>
          {user ? (
            <button
              onClick={() => {
                signOut()
                onClose()
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                width: '100%',
                padding: '14px 16px',
                borderRadius: 14,
                border: 'none',
                background: 'transparent',
                fontFamily: FB,
                fontSize: 16,
                fontWeight: 500,
                color: C.ink2,
                cursor: 'pointer',
              }}
            >
              <LogOut size={22} color={C.ink2} style={{ flexShrink: 0 }} />
              Cerrar sesión
            </button>
          ) : (
            <button
              onClick={() => go('/login')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                width: '100%',
                padding: '14px 16px',
                borderRadius: 14,
                border: 'none',
                background: `${C.teal}12`,
                color: C.teal,
                fontFamily: FB,
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              <LogIn size={22} color={C.teal} style={{ flexShrink: 0 }} />
              Ingresar
            </button>
          )}
        </div>
      </div>
    </>
  )
}
